import { useEffect, useState } from 'react';
import { useEvents } from '../data/useEvents';
import {
  seriesFieldsFromForm, occurrenceFieldsFromForm, initialEventForm, seededDuration,
  eventCanSave, eventPriceError, recurrenceChanged as recurrenceChangedOf,
  deletesEntriesAfter as deletesEntriesAfterOf, planEventSave, planRuleUpdate, chooseEventCategory,
  eventIntervalError,
  eventMaxStudentsError,
} from '../domain/eventSeries';
import { findConflicts, singleEntrySchedule } from '../domain/conflicts';
import { clampAmountDecimals } from '../domain/money';
import { defaultEventCategory } from '../domain/categories';
import { useCurrency } from '../context/LocaleContext';
import { useSettings } from '../data/useSettings';

// The event drawer's state and the three ways it can be saved.
//
// The form is built in a lazy initialiser, which runs once per mount. The
// caller must therefore mount the drawer only while it is open — kept mounted,
// the initial values freeze at the first render and no later prop can move
// them.
//
// The rules this hook applies — the form a drawer opens with, whether Save is
// enabled, what Save does for each scope, and what an edit of the rule does to
// the overrides under it — are `src/domain/eventSeries.js`, so the phones'
// drawers answer them the same way. What stays here is the state, the
// dialogs, and the writes.

export default function useEventForm({
  // `loaded` mirrors `useCategories`' own (see the seeding effect below). It
  // defaults true because most callers, every unit test among them, hand this
  // hook a final `categories` list; `EventDrawer` reads from the provider and
  // passes the real value. `students` is the studio's list as `useStudents`
  // hands it out, each row carrying `durationMin`; only the duration seeding
  // reads it, and it defaults empty, which seeds nothing.
  editing, initialDate, initialTime, initialDuration, shape, categories = [], loaded = true,
  students = [], onDone,
}) {
  const {
    series, conflictSchedules, weeklyHoldDays, createSeries, updateSeries, overrideOccurrence,
    dropOverridesFrom, dropAllOverrides,
  } = useEvents();

  // What a typed price means, from the studio's locale: "3000" is 3000 minor
  // units in yen and 300000 in dollars, and the parse has to agree with the
  // display.
  const currency = useCurrency();

  // Whether to look for double bookings at all. MMS makes it a studio setting
  // ("Check for scheduling conflicts when adding/editing calendar events"), and
  // off means the check does not run.
  const { settings } = useSettings();
  const checkConflicts = settings.scheduling.checkConflicts;

  const [form, setForm] = useState(() =>
    initialEventForm({
      editing, initialDate, initialTime, initialDuration, shape, currency, categories,
    }));

  // Whether the teacher has picked a category themselves on a new event: what
  // stops the seeding effect below re-deriving the default. A fact about the
  // teacher, because the form holds a value from the moment it is constructed.
  const [categoryTouched, setCategoryTouched] = useState(false);

  // Re-derive a new event's category until the teacher chooses one.
  //
  // `useCategories` hands out `DEFAULT_CATEGORIES` as a placeholder before it
  // has read this studio's collection (#153), and `initialEventForm` seeds from
  // whatever list this drawer mounted with. Matching on the id alone would keep
  // a stale label — a studio that renamed Lesson to "Piano Lesson" still has id
  // `lesson` (#141) — so the default is re-derived on every list until
  // `chooseCategory` sets `categoryTouched`. `loaded` stays in the condition
  // because a fresh listener's first snapshot is routinely empty and from cache
  // (#153), and there is no reason to seed from the placeholder even once.
  //
  // Never on an edit: an existing event whose `categoryId` is absent or dangles
  // is for a person to decide, as `relabelCategory` refuses to guess.
  useEffect(() => {
    if (editing || categoryTouched || !loaded || !categories.length) return;
    const seed = defaultEventCategory(categories);
    if (!seed) return;
    setForm((f) => (
      f.categoryId === seed.id && f.categoryLabel === seed.label
        ? f
        : { ...f, categoryId: seed.id, categoryLabel: seed.label }
    ));
  }, [editing, categories, loaded, categoryTouched]);

  // Whether the teacher has moved the Duration field on this form. Set by
  // `set` below, for the reason `categoryTouched` gives.
  const [durationTouched, setDurationTouched] = useState(false);

  // ── A new lesson takes its duration from the student attending it (#241) ──
  //
  // `price_occurrence` multiplies the rate by the occurrence's minutes, so a
  // student's `defaultDurationMin` has to reach the form. MMS differs (#285):
  // Add New Event seeds neither Duration nor Category from the attendee, and
  // Quick-Add Lesson, which has neither field, takes both from the student.
  // This form seeds the duration, under three rules:
  //
  //  1. Never over a value the teacher supplied: not over a typed duration
  //     (`durationTouched`), and not over `initialDuration`, which is the
  //     length of a slot dragged on the calendar.
  //  2. Never on an edit. An existing event's duration is a fact about a
  //     lesson that may already have been taught and charged.
  //  3. Only when the attendees agree. With two attendees whose defaults
  //     differ the field keeps what it has and the teacher decides.
  //
  // The duration is mandatory at both student forms and in `firestore.rules`
  // (#352); a student without one has a null `studentDurationMin`, which
  // `seededDuration` filters out, so it seeds nothing.
  useEffect(() => {
    if (editing || durationTouched || initialDuration != null) return;
    // Rule 3, and the null filter below it, are `seededDuration`.
    const agreed = seededDuration(form.studentIds, students);
    if (agreed == null) return;
    setForm((f) => (f.durationMin === agreed ? f : { ...f, durationMin: agreed }));
  }, [editing, durationTouched, initialDuration, form.studentIds, students]);

  const [saving, setSaving] = useState(false);
  const [scopeOpen, setScopeOpen] = useState(false);
  // The conflict warning, and the save it is standing in front of. Non-null
  // means the dialog is up: `result` is what domain/conflicts.js found, and
  // `proceed` is the write it will run if the teacher chooses to ignore it.
  const [conflict, setConflict] = useState(null);

  // Every write to the form, plus the one fact the duration seeding needs: has
  // the teacher moved the Duration field themselves.
  //
  // The event price is truncated at the keystroke, not rounded (#308):
  // `from_decimal` rounds half-up, so `50.999` would bank $51.00.
  // `clampAmountDecimals` is what MMS does to a third decimal on its Add
  // Payment drawer — it swallows the keystroke (#246) — applied to every
  // amount box. Clamped here because `set` is the single way into this form's
  // state. The field's `step` is `amountStep(currency)` at the input, not a
  // hard-coded `0.01`, which would mark a legitimate third decimal `:invalid`
  // in KWD.
  const set = (key, value) => {
    if (key === 'durationMin') setDurationTouched(true);
    const next = key === 'priceOverride' ? clampAmountDecimals(value, currency) : value;
    setForm((f) => ({ ...f, [key]: next }));
  };

  // The document fields this form describes. Wrapped because two studio-level
  // values feed it — the currency a typed price is parsed in, and how many days
  // a temporary weekly hold runs — and a call site that forgot either would
  // produce a plausible, wrong document. `seriesFieldsFromForm` carries
  // `allDay` and `requiresMakeupCredit` (#160, #312).
  const fieldsFromForm = () => seriesFieldsFromForm(form, currency, weeklyHoldDays);

  // `canSave` is `eventCanSave` in domain/eventSeries.js:
  //
  // An event nobody attends is legitimate — a non-teaching block, or a slot
  // held open — so attendees are only required where the form asks for them.
  //
  // A "Repeat Until" before the start date describes a series with no lessons,
  // which would look exactly like an event never created. MMS greys out the
  // earlier days in its picker; the date input's `min` does the same, and this
  // catches a typed one.
  //
  // A duration firestore.rules will not take cannot leave this drawer (#137,
  // #148): a typed 37.5, or more than 1440 minutes, would be denied as a
  // permission error with nothing on screen about duration. Checked whatever
  // `shape.duration` says, because a quick-add hides the field but still
  // carries the value.
  const canSave = eventCanSave(form, shape);

  // The "Specify event price" box, PricingSection's own field (#171).
  // `priceOverrideMinor` reaches `optionalMinor` in firestore.rules, which
  // carries MAX_MONEY_MINOR (#164); past it the write would come back as a bare
  // permission-denied.
  //
  // Not folded into `canSave`: it follows the convention #115 settled for money
  // and date fields (`entryErrors` in useExpenseEntryForm.js) — Save stays
  // clickable, the field shows the sentence, and `save`/`saveWithScope` refuse
  // the write.
  //
  // Only judged in the `fixed` rule: `seriesFieldsFromForm` discards what is
  // typed here for `student` or `none`, so a stale value from a rule the
  // teacher switched away from must not block a save.
  const priceErrorText = eventPriceError(form, currency);

  // Editing one lesson of a repeating series is ambiguous, so it gets asked
  // about. A one-time event, or a new one, saves straight away
  // (`isRecurringEdit`, which `planEventSave` reads).

  // The rule as it stands, which an edit is compared against.
  const savedRule = editing ? series.find((s) => s.id === editing.seriesId) : null;

  // Which dialog Save opens, decided by the recurrence having changed and by
  // nothing else.
  //
  // MMS asks the two-radio scope question only about a per-occurrence edit.
  // Change the frequency, the interval or the repeat bound and it shows
  // "Update Future Entries" instead — one sentence, one Update button, no
  // radios — from the first occurrence or a middle one. An edit that touches
  // both is driven by the recurrence: the whole rule is updated and no scope is
  // asked for.
  //
  // Turning a repeating series into a one-time event, or into a weekly hold,
  // keeps the scope question: "this and following" answers it by splitting.
  const recurrenceChanged = recurrenceChangedOf({
    editing, savedRule, form, currency, weeklyHoldDays,
  });

  // MMS warns in red under "Repeat Until", while the earlier date is typed,
  // "You will be deleting any entry after 9/21/2026": the entries past the new
  // end go, and re-extending the run does not bring their overrides back.
  //
  // Only when entries actually go: a bound pulled back past no lesson deletes
  // nothing. A warning, not a blocker: ending a term early is a thing teachers
  // mean to do.
  const deletesEntriesAfter = deletesEntriesAfterOf(form, savedRule);

  const run = async (fn) => {
    setSaving(true);
    try { await fn(); onDone?.(); } finally { setSaving(false); }
  };

  // The whole rule this form describes, as a schedule — the shape
  // domain/conflicts.js compares. Everything but two fields comes from the
  // form:
  //
  //   exceptions  live on the rule document and no field here touches them, so
  //               a series with a removed entry keeps the hole (#152), and the
  //               check sees the calendar's gaps.
  //   overrides   the calendar as it stands: a moved entry collides where it
  //               moved to. An edit that shifts the rule's dates drops these
  //               and the exceptions (see updateRule below), so for that case
  //               the check reads a calendar slightly ahead of itself. The
  //               alternative warns about lessons that are not on the calendar.
  const ruleSchedule = (fields) => ({
    seriesId: editing?.seriesId ?? null,
    ...fields,
    exceptions: savedRule?.exceptions || [],
    overrides: conflictSchedules.find((c) => c.seriesId === editing?.seriesId)?.overrides || {},
  });

  // Everything the drawer saves goes through here.
  //
  // The check runs before the write, so Cancel means what MMS says — "cancel
  // to undo the change" — and the drawer stays open with the edits in it.
  //
  // `ignore` says what of the stored calendar this save replaces, so the event
  // is not reported as conflicting with itself. Null lets findConflicts default
  // to the proposal's own series, which is right for every save to a rule.
  const guarded = async (proposed, ignore, commit) => {
    if (!checkConflicts) { await run(commit); return; }
    const result = findConflicts(proposed, conflictSchedules, ignore ? { ignore } : {});
    if (result.count > 0) { setConflict({ result, proceed: () => run(commit) }); return; }
    await run(commit);
  };

  // "Ignore & Continue" — MMS's primary action. The warning blocks nothing.
  const confirmConflict = async () => {
    const proceed = conflict?.proceed;
    setConflict(null);
    if (proceed) await proceed();
  };

  const cancelConflict = () => setConflict(null);

  // Applying an edit to the rule itself, and clearing up after it.
  //
  // Two saves reach the rule rather than one occurrence: editing a one-time
  // event, and "this and following" from a series' own first occurrence. Either
  // can move the Date field, which moves every date the rule produces, while an
  // override is keyed by the rule date it belonged to.
  //
  // MMS drops them: shifting a series wholesale to another weekday brings the
  // lessons back at the series' defaults, an override neither re-keyed nor
  // stranded. All of them, not only those from the old start on — an override
  // can sit before the current start, left by an earlier shift — and even where
  // a whole-week shift leaves some old dates in the new set, since keeping them
  // would move a lengthened lesson onto a different week. Frequency and
  // interval count as a move for the same reason; that part is reasoned, not
  // observed in MMS.
  //
  // A shortened "Repeat Until" culls the excluded tail and only that: MMS
  // deletes those entries, and extending the run back returns them at the
  // series' default duration. Every surviving date keeps its key, so
  // `dropAllOverrides` there would throw away a lesson still on the calendar.
  // `shortenedFrom` returns the day after the new end date (the bound is
  // inclusive); a move has already dropped everything, so the branches are
  // exclusive.
  //
  // Exceptions (#152) go the same way: a removed entry is a rule date in
  // `exceptions`, keyed like an override. A stale one is inert until the rule
  // is shifted back, when a lesson would silently disappear. Reasoned from the
  // override rule; MMS deletes the lesson outright and has no exception.
  //
  // The decision is `planRuleUpdate` in domain/eventSeries.js. It reads the rule
  // when the write runs, not when Save was pressed, so a conflict dialog left
  // open while the series changes still cleans up against the rule as it is.
  const updateRule = async (fields) => {
    const before = series.find((s) => s.id === editing.seriesId);
    const plan = planRuleUpdate(before, fields);
    if (plan.dropAllOverrides) await dropAllOverrides(editing.seriesId);
    else if (plan.dropOverridesFrom) await dropOverridesFrom(editing.seriesId, plan.dropOverridesFrom);
    await updateSeries(
      editing.seriesId,
      plan.exceptions == null ? fields : { ...fields, exceptions: plan.exceptions },
    );
  };

  // What each Save does is `planEventSave` in domain/eventSeries.js.
  const plan = (scope = null) => planEventSave({
    form, editing, savedRule, scope, currency, weeklyHoldDays,
  });

  const save = async () => {
    // The field already shows the sentence; this stops the write itself
    // (#171), as `entryErrors`' callers do on the expense form.
    const p = plan();
    if (p.action === 'refuse') return;
    if (p.action === 'askScope' || p.action === 'askRecurrence') { setScopeOpen(true); return; }
    const fields = fieldsFromForm();
    await guarded(ruleSchedule(fields), null, async () => {
      if (p.action === 'updateRule') await updateRule(fields);
      else await createSeries(fields);
    });
  };

  const saveWithScope = async (scope) => {
    // Same check as `save` (#171): the only other door to the write.
    const p = plan(scope);
    if (p.action === 'refuse') { setScopeOpen(false); return; }
    setScopeOpen(false);
    const fields = fieldsFromForm();

    if (p.action === 'updateRule') {
      // Two ways here, told apart only by the start date `planEventSave`
      // hands back.
      //
      // A recurrence change: "Update Future Entries" offers no scope, so the
      // dialog's default is ignored. The rule's start cannot come from the
      // form: on a middle occurrence the Date field shows that lesson's day,
      // and writing it as the rule's start would delete every lesson before
      // it, which MMS's dialog, mentioning only future entries, does not.
      //
      // "This and following" from the series' own first occurrence means all
      // of it. Splitting here would leave the original stopped the day before
      // it began, a husk that produces no lessons.
      const ruleFields = { ...fields, startDate: p.startDate };
      await guarded(ruleSchedule(ruleFields), null, () => updateRule(ruleFields));
      return;
    }

    if (p.action === 'override') {
      // An override on this date alone — including a move, if the teacher
      // changed the Date field. The rule date is passed so the override can
      // record the displacement.
      //
      // Only this one entry is excluded from the comparison, not the series:
      // moving this Tuesday onto a Wednesday the same series occupies is a
      // double booking.
      await guarded(
        singleEntrySchedule({
          seriesId: editing.seriesId,
          date: form.startDate,
          startTime: fields.startTime,
          durationMin: fields.durationMin,
          studentIds: fields.studentIds,
          categoryLabel: fields.categoryLabel,
        }),
        { seriesId: editing.seriesId, ruleDate: p.ignoreRuleDate },
        // `allDay` is in `OCCURRENCE_FIELDS` (#312), so one entry of a series
        // can be all-day on its own — "no lesson on the 14th, the studio is
        // shut".
        () => overrideOccurrence(
          editing.occurrenceId,
          occurrenceFieldsFromForm(form, editing.occurrenceDate, currency),
        ),
      );
      return;
    }

    // "This and following" splits the series rather than editing it in
    // place: the old rule is stopped the day before this occurrence and a
    // new one starts here. Editing in place would rewrite lessons already
    // taught and, through attendance, already charged.
    //
    // The cut is on the rule date, which the old rule produces and its
    // overrides are keyed by. The new rule starts on whatever the form says,
    // so "move this and every lesson after it to Thursdays" works as in MMS:
    // the remainder shifts to the new date's weekday and keeps the original
    // end date. The old rule's overrides from this date on are dropped.
    //
    // The conflict check excludes the old series entirely. Its entries before
    // the cut cannot collide with a rule that starts at the cut, unless the
    // teacher also dragged the Date field back past it; that case
    // under-reports.
    //
    // The tail inherits the original rule's time zone (#47): the same lessons
    // must not shift because the studio's setting changed since. Null for a
    // series written before the field, which createSeries stamps from the
    // studio.
    const tailZone = series.find((s) => s.id === editing.seriesId)?.timeZone || null;
    await guarded(ruleSchedule(fields), null, async () => {
      await dropOverridesFrom(editing.seriesId, p.cut);
      await updateSeries(editing.seriesId, { endDate: p.endDate });
      await createSeries({ ...fields, timeZone: tailZone });
    });
  };

  // A category marked not-billable forces the event to no charge, so a recital
  // cannot bill the family because the student is on a per-lesson rate.
  //
  // Keyed on the id (#128): a label cannot tell two categories of one name
  // apart. An id naming nothing changes nothing — the select's only
  // non-category option is the disabled placeholder — rather than writing a
  // null pair that would uncategorise the event. The rule is
  // `chooseEventCategory` in domain/eventSeries.js.
  const chooseCategory = (id) => {
    if (!categories.some((c) => c.id === id)) return;
    // Stops the seeding effect above re-deriving over a real choice.
    setCategoryTouched(true);
    setForm((f) => chooseEventCategory(f, id, categories) || f);
  };

  return {
    form, set, canSave, saving, scopeOpen, setScopeOpen, save, saveWithScope, chooseCategory,
    recurrenceChanged, deletesEntriesAfter,
    // PricingSection's own field message (#171), computed here because this
    // hook has the currency the figure is typed in.
    priceError: priceErrorText,
    // Repeats Every's field message: the rules deny a fraction or a negative,
    // and Save is held shut on it, so the field says why.
    intervalError: eventIntervalError(form),
    // Maximum Students' (#766), on the same terms.
    maxStudentsError: eventMaxStudentsError(form, currency),
    // The conflict warning: what was found, and the two ways out of it.
    conflict: conflict?.result || null, confirmConflict, cancelConflict,
    // So the "up to N days" the hold radio promises is the number the save
    // actually uses.
    weeklyHoldDays,
  };
}
